import {
    AUTH0_AUDIENCE,
    AUTH0_CLIENT_ID,
    AUTH0_DOMAIN,
    BASE_BACKEND_URL,
    IS_TEST,
    SSO_CONNECTION,
} from 'env';
import i18next from 'i18next';

import error_handler from 'helpers/error';
import { DEFAULT_LOCALE } from 'helpers/i18n';
import { AuthData as AuthDataQueryResponse } from 'redesign/apiREST/mutations/useLogin';
import localStorage from 'redesign/helpers/localStorage';
import AuthClient from 'services/api/auth_client';

export const PRICEMOOV_AUTH_KEY = 'pricemoov_auth';

export const REQUESTED_URL = 'REQUESTED_URL';

// Auth0 configuration
export const AUTH0_CLIENT_CONFIG: auth0.AuthOptions = {
    domain: AUTH0_DOMAIN || '',
    clientID: AUTH0_CLIENT_ID || '',
    redirectUri: `${BASE_BACKEND_URL}/login/sso/`,
    responseType: 'code',
    responseMode: 'form_post',
    audience: AUTH0_AUDIENCE || '',
};

export interface AuthData extends AuthDataQueryResponse {
    updated_at: number;
}

/**
 * Bridge for the backend migration from `user.features` to `user.permissions`.
 *
 * While the backend still exposes the legacy `features` array on the auth
 * payload, copy it into `permissions` so the frontend (which only reads
 * `user.permissions`) keeps working. Remove this shim once the backend ships
 * `user.permissions`.
 */
export const normalizeAuthData = <T extends AuthData | AuthDataQueryResponse>(data: T): T => {
    if (!data?.user) return data;
    const user = data.user as typeof data.user & { features?: string[] };
    if (!user.permissions && Array.isArray(user.features)) {
        return { ...data, user: { ...data.user, permissions: user.features } };
    }
    return data;
};

/**
 *
 * @returns The persisted AuthData
 */
export const restoreAuthData = (): AuthData | null => {
    const storedData = localStorage.get(PRICEMOOV_AUTH_KEY);

    if (storedData) {
        return normalizeAuthData(JSON.parse(storedData));
    }
    return null;
};

/**
 *
 * @param {AuthData} persist the authData in the local storage
 */
export const persistAuthData = (authData: AuthData) => {
    localStorage.set(
        PRICEMOOV_AUTH_KEY,
        JSON.stringify({
            ...normalizeAuthData(authData),
            updated_at: Date.now() / 1000,
        })
    );
};

/**
 * Persist changes of the authenticated user's profile without touching `updated_at`,
 * which tracks the last token refresh and drives `isExpired`
 * @param {AuthData} authData the auth data with the updated user
 */
export const persistAuthUser = (authData: AuthData) => {
    localStorage.set(PRICEMOOV_AUTH_KEY, JSON.stringify(normalizeAuthData(authData)));
};

/**
 * Clear the persisted authData from the local storage
 */
export const clearAuthData = () => {
    localStorage.remove(PRICEMOOV_AUTH_KEY);
};

export const clearAuth0Cookies = () => {
    document.cookie.split('; ').forEach(cookie => {
        const key = cookie.split('=')[0];
        // Remove auth0 and session cookies (the latter is used for Sisense SSO)
        if (key.includes('com.auth0.auth') || key === 'sessionid') {
            document.cookie = `${key}=;expires=Thu, 01 Jan 1970 00:00:01 GMT ;`;
        }
    });
};

/**
 * Initialize the pricemoov App
 * @param authData
 */
export const initializeApp = async (authData: AuthData) => {
    const { user } = authData;
    if (!IS_TEST) {
        // Set language during logging
        await i18next.changeLanguage(user?.language || DEFAULT_LOCALE);
        await i18next.loadNamespaces('pricemoov');
    }
};

export const clearAuthCache = () => {
    clearAuthData();
    clearAuth0Cookies();
    sessionStorage.clear();
};

export function persistLastVisitedURL(newURL: string) {
    sessionStorage.setItem(REQUESTED_URL, newURL);
}

export function restoreLastVisitedURL() {
    return sessionStorage.getItem(REQUESTED_URL);
}

export function clearLastVisitedURL() {
    sessionStorage.removeItem(REQUESTED_URL);
}

// @TODO add useSendPasswordResetEmail hook (moon)
export const sendPasswordResetEmail = async (params: { email: string }) => {
    const { email } = params;
    const client = new AuthClient();
    try {
        await client.sendPasswordResetEmail(email);
    } catch (error) {
        error_handler.notify(error);
    }
};

/**
 * return the sso connection env var
 */
export function getSSOConnection(): string {
    return SSO_CONNECTION;
}
